import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { feedbackSchema, type FeedbackInput } from '../lib/validate'
import { submitFeedback, type ApiSuccess } from '../lib/api-client'
import { errorToMessage } from '../lib/error-message'
import { t, type Lang } from '../lib/i18n'
import { FormField, inputClasses, textareaClasses } from './form-field'
import { SubmitButton } from './submit-button'
import { SuccessState } from './success-state'
import { Turnstile } from './turnstile'

const formSchema = feedbackSchema.omit({ turnstileToken: true })
type FormData = z.infer<typeof formSchema>

type State =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'success'; result: ApiSuccess }
  | { status: 'error'; message: string }

interface FeedbackFormProps {
  lang: Lang
  type: FeedbackInput['type']
  slug: string
}

export function FeedbackForm({ lang, type, slug }: FeedbackFormProps) {
  const [state, setState] = useState<State>({ status: 'idle' })
  const [token, setToken] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { type, slug, lang, message: '', email: '' },
    mode: 'onBlur',
  })

  const message = watch('message') ?? ''

  const onSubmit = async (data: FormData) => {
    if (!token) {
      setState({ status: 'error', message: t(lang, 'error.turnstile_pending') })
      return
    }
    setState({ status: 'submitting' })
    const res = await submitFeedback({ ...data, turnstileToken: token })
    if (res.ok) {
      setState({ status: 'success', result: res })
    } else {
      setState({ status: 'error', message: errorToMessage(res, lang) })
      setToken(null)
    }
  }

  if (state.status === 'success') {
    return (
      <SuccessState
        lang={lang}
        variant="feedback"
        issueNumber={state.result.issueNumber}
        issueUrl={state.result.url}
        onReset={() => {
          reset()
          setToken(null)
          setState({ status: 'idle' })
        }}
      />
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <input type="hidden" {...register('type')} />
      <input type="hidden" {...register('slug')} />

      <FormField
        label={t(lang, 'feedback.field.message')}
        required
        helper={t(lang, 'feedback.field.message.help')}
        error={errors.message?.message}
        counter={{ current: message.length, max: 1000 }}
      >
        <textarea
          {...register('message')}
          className={textareaClasses}
          maxLength={1000}
          rows={6}
        />
      </FormField>

      <FormField
        label={t(lang, 'feedback.field.email')}
        helper={t(lang, 'feedback.field.email.help')}
        error={errors.email?.message}
      >
        <input
          {...register('email')}
          type="email"
          className={inputClasses}
          autoComplete="email"
          inputMode="email"
        />
      </FormField>

      <Turnstile onToken={setToken} onExpired={() => setToken(null)} />

      {state.status === 'error' && (
        <p role="alert" className="text-sm text-danger">
          {state.message}
        </p>
      )}

      <SubmitButton
        isSubmitting={state.status === 'submitting'}
        disabled={!token}
        label={t(lang, 'feedback.submit')}
        loadingLabel={t(lang, 'feedback.submit.loading')}
      />
    </form>
  )
}
